import { company, socials } from '@awpmarine/core/company';
import { UiMessagesProvider } from '@awpmarine/ui/components/ui-messages';
import { HeadContent, Outlet, Scripts, createRootRoute } from '@tanstack/react-router';

import { Footer } from '@/components/footer';
import { Header } from '@/components/header';
import { languageTag } from '@/lib/locale';
import { m } from '@/paraglide/messages';
import { config } from '@/site-config';
import '@/lib/dash0'; // Side-effect import: initializes browser monitoring before the first render
import '@/lib/logger'; // Side-effect import: routes browser logs to the console and Dash0
import '@/lib/plausible'; // Side-effect import: starts page view tracking

import globalsCss from '@/globals.css?url';

const organizationLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': company.legalName,
  'alternateName': company.shortName,
  'url': config.siteUrl,
  'logo': `${config.siteUrl}/logo.svg`,
  'email': company.emails.default,
  'telephone': company.phones[0].tel,
  'description': m.site_description(),
  'areaServed': 'Worldwide',
  // The registered office: the trading address is deliberately not published.
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': [company.registeredAddress.line1, company.registeredAddress.line2].filter(Boolean).join(', '),
    'addressLocality': company.registeredAddress.city,
    'postalCode': company.registeredAddress.postcode,
    'addressCountry': 'GB',
  },
  'sameAs': Object.values(socials).map((social) => social.url),
});

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'theme-color', content: config.themeColor },
      { title: config.title },
      { name: 'description', content: m.site_description() },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: m.site_description() },
      { property: 'og:url', content: config.siteUrl },
      { property: 'og:site_name', content: config.name },
    ],
    links: [
      { rel: 'stylesheet', href: globalsCss },
      { rel: 'icon', href: '/icon.png', type: 'image/png', sizes: '32x32' },
      { rel: 'apple-touch-icon', href: '/apple-icon.png', type: 'image/png', sizes: '180x180' },
      { rel: 'manifest', href: '/manifest.webmanifest' },
    ],
    // JSON-LD structured data: a static, code-controlled payload with no user input.
    scripts: [{ type: 'application/ld+json', children: JSON.stringify(organizationLd()) }],
  }),
  component: RootLayout,
});

function RootLayout() {
  return (
    <html lang={languageTag()} className='font-sans'>
      <head>
        <HeadContent />
      </head>
      <body className='antialiased'>
        <UiMessagesProvider
          messages={{
            themeLight: m.ui_theme_light(),
            themeDark: m.ui_theme_dark(),
            themeSystem: m.ui_theme_system(),
            timeAgoWhen: m.ui_time_ago_when(),
            timeAgoLocal: m.ui_time_ago_local(),
            timeAgoUtc: m.ui_time_ago_utc(),
          }}
        >
          <div className='min-h-screen bg-white'>
            <Header />
            <main>
              <Outlet />
            </main>
            <Footer />
          </div>
        </UiMessagesProvider>
        <Scripts />
      </body>
    </html>
  );
}
